import React, { useState, useMemo, useEffect } from "react";
import { Copy, Link as LinkIcon, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * Del-melding-bygger for skapere. Lar skaperen velge et hjul fra dropdown,
 * skrive (eller redigere) en fritekst som lagres lokalt i nettleseren, og
 * få en kopi-klar melding: ledetekst + link + sluttetekst.
 *
 * Lagrer ikke noe i backend – det er kun et hjelpeverktøy for klipp-og-lim.
 */
const STORAGE_KEY = "ne_wheel_share_texts";

const loadTexts = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
};
const saveTexts = (obj) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); }
  catch { /* private mode */ }
};

export default function WheelShareBuilder({ wheels = [], creatorUsername }) {
  const activeWheels = useMemo(() => wheels.filter(w => w.status === "active"), [wheels]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [allTexts, setAllTexts] = useState(loadTexts);
  const [textBefore, setTextBefore] = useState("🎰 Spinn lykkehjulet mitt og vinn premier!");
  const [textAfter, setTextAfter] = useState("Send meg en DM hvis du vinner noe spennende 💌");

  // Når wheels lastes (asynkront fra parent), velg det første aktive
  useEffect(() => {
    if (!selectedSlug && activeWheels.length > 0) {
      const slug = activeWheels[0].slug;
      setSelectedSlug(slug);
      const saved = loadTexts()[slug] || {};
      setTextBefore(saved.before ?? "🎰 Spinn lykkehjulet mitt og vinn premier!");
      setTextAfter(saved.after ?? "Send meg en DM hvis du vinner noe spennende 💌");
    }
  }, [activeWheels, selectedSlug]);

  // Når hjul endres, hent lagret tekst for det hjulet
  const onWheelChange = (slug) => {
    setSelectedSlug(slug);
    const saved = allTexts[slug] || {};
    setTextBefore(saved.before ?? "🎰 Spinn lykkehjulet mitt og vinn premier!");
    setTextAfter(saved.after ?? "Send meg en DM hvis du vinner noe spennende 💌");
  };

  const persist = (next) => {
    const updated = { ...allTexts, [selectedSlug]: { ...(allTexts[selectedSlug] || {}), ...next } };
    setAllTexts(updated);
    saveTexts(updated);
  };

  const selectedWheel = activeWheels.find(w => w.slug === selectedSlug);
  const link = selectedWheel ? `${window.location.origin}/lykkehjul/${selectedSlug}` : "";
  const fullMessage = [textBefore, link, textAfter].filter(Boolean).join("\n\n");

  const copyAll = async () => {
    if (!fullMessage) return toast.error("Ingen melding å kopiere");
    try {
      await navigator.clipboard.writeText(fullMessage);
      toast.success("Melding kopiert til utklippstavle!");
    } catch { toast.error("Klarte ikke å kopiere"); }
  };
  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Lenke kopiert!");
    } catch { toast.error("Klarte ikke å kopiere"); }
  };

  if (activeWheels.length === 0) return null;

  return (
    <section className="panel mb-8 p-5" data-testid="wheel-share-builder">
      <h2 className="font-meta tracking-[0.2em] text-[11px] text-[var(--cream-soft)] mb-1 flex items-center gap-2">
        <MessageSquare className="h-3.5 w-3.5 text-[var(--magenta)]" /> DEL-MELDING-BYGGER
      </h2>
      <p className="text-xs text-[var(--cream-soft)] mb-4 leading-relaxed">
        Velg hjul, tilpass tekst over og under lenken, og kopier hele meldingen til utklippstavlen.
        Lim den inn der du selger spinn-koder (OnlyFans, Snapchat osv.). Tekstene huskes i din nettleser.
      </p>

      <div className="space-y-3">
        <div>
          <label className="block font-meta text-[10px] tracking-[0.18em] text-[var(--cream-soft)] mb-1.5">VELG HJUL</label>
          <select
            value={selectedSlug}
            onChange={(e) => onWheelChange(e.target.value)}
            className="w-full h-10 px-3 bg-[var(--noir)] border border-white/10 text-[var(--cream)] text-sm"
            data-testid="wheel-share-select"
          >
            {activeWheels.map(w => (
              <option key={w.slug} value={w.slug}>{w.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-meta text-[10px] tracking-[0.18em] text-[var(--cream-soft)] mb-1.5">TEKST FØR LENKE</label>
          <textarea
            value={textBefore}
            onChange={(e) => { setTextBefore(e.target.value); persist({ before: e.target.value }); }}
            rows={2}
            placeholder="f.eks. 🎰 Spinn lykkehjulet mitt og vinn premier!"
            className="w-full p-3 bg-[var(--noir)] border border-white/10 text-[var(--cream)] text-sm"
            data-testid="wheel-share-before"
          />
        </div>

        <div>
          <label className="block font-meta text-[10px] tracking-[0.18em] text-[var(--cream-soft)] mb-1.5">GENERERT LENKE</label>
          <div className="flex gap-2">
            <input
              readOnly
              value={link}
              className="flex-1 h-10 px-3 bg-[var(--noir-elev)] border border-white/10 text-[var(--magenta)] text-sm font-mono"
              data-testid="wheel-share-link"
            />
            <button
              type="button"
              onClick={copyLink}
              className="px-3 h-10 border border-white/10 text-[var(--cream)] hover:bg-white/5 inline-flex items-center gap-1.5 text-xs"
              data-testid="wheel-share-copy-link-btn"
              title="Kopier kun lenken"
            >
              <LinkIcon className="h-3.5 w-3.5" /> Lenke
            </button>
          </div>
        </div>

        <div>
          <label className="block font-meta text-[10px] tracking-[0.18em] text-[var(--cream-soft)] mb-1.5">TEKST ETTER LENKE</label>
          <textarea
            value={textAfter}
            onChange={(e) => { setTextAfter(e.target.value); persist({ after: e.target.value }); }}
            rows={2}
            placeholder="f.eks. Send meg en DM hvis du vinner noe spennende 💌"
            className="w-full p-3 bg-[var(--noir)] border border-white/10 text-[var(--cream)] text-sm"
            data-testid="wheel-share-after"
          />
        </div>

        <div>
          <label className="block font-meta text-[10px] tracking-[0.18em] text-[var(--cream-soft)] mb-1.5">FORHÅNDSVISNING</label>
          <pre className="whitespace-pre-wrap break-words bg-[var(--noir-elev)] border border-[var(--magenta)]/20 p-3 text-sm text-[var(--cream)] font-sans" data-testid="wheel-share-preview">
{fullMessage}
          </pre>
        </div>

        <button
          type="button"
          onClick={copyAll}
          className="w-full py-3 bg-[var(--magenta)] text-black font-meta tracking-[0.2em] text-xs hover:opacity-90 inline-flex items-center justify-center gap-2"
          data-testid="wheel-share-copy-all-btn"
        >
          <Copy className="h-4 w-4" /> KOPIER HELE MELDINGEN
        </button>
      </div>
    </section>
  );
}
